import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ChannelPrefs,
  type NotificationPreferences,
  type NotificationPreferencesUpdate,
  notificationApi,
} from '@/lib/api-client';
import { notify } from '@/lib/notify';
import { ErrorState } from '@/components/core/common/boundaries/ErrorState';

// ============================================================================
// Toggle row component
// ============================================================================

interface ToggleRowProps {
  label: string;
  description: string;
  enabled: boolean;
  onToggle: () => void;
}

function ToggleRow({ label, description, enabled, onToggle }: ToggleRowProps) {
  return (
    <div className='flex items-center justify-between gap-4 py-3'>
      <div>
        <p className='text-sm font-medium text-foreground'>{label}</p>
        <p className='text-xs text-muted-foreground'>{description}</p>
      </div>
      <button
        type='button'
        role='switch'
        // The row's visible text is a sibling, not a child, so the switch had
        // NO accessible name — a screen-reader user heard "switch, checked"
        // with nothing to say what it controlled. axe `button-name`.
        aria-label={label}
        aria-checked={enabled}
        onClick={onToggle}
        className={`tap-target relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ${
          enabled ? 'bg-primary' : 'bg-secondary'
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-card shadow-sm transition-transform duration-200 ${
            enabled ? 'translate-x-[22px]' : 'translate-x-[2px]'
          } mt-[2px]`}
        />
      </button>
    </div>
  );
}

// ============================================================================
// Email toggle config
// ============================================================================

type EmailToggleKey = keyof Omit<NotificationPreferences, 'all_enabled'>;

const EMAIL_TOGGLES: Array<{
  key: EmailToggleKey;
  label: string;
  description: string;
}> = [
  {
    key: 'motivation',
    label: 'Motivational Nudges',
    description: "Occasional encouragement when you've been inactive.",
  },
  {
    key: 'concepts_due_today',
    label: 'Concepts Due Today',
    description: 'Daily reminder when Mindspace concepts are ready for review.',
  },
];

// ============================================================================
// Main component
// ============================================================================

export function NotificationsSection() {
  const queryClient = useQueryClient();

  const {
    data: prefs,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: notificationApi.getPreferences,
  });

  const { mutate: updatePrefs } = useMutation({
    mutationFn: (updates: NotificationPreferencesUpdate) =>
      notificationApi.updatePreferences(updates),
    onMutate: async updates => {
      await queryClient.cancelQueries({
        queryKey: ['notification-preferences'],
      });
      const previous = queryClient.getQueryData<NotificationPreferences>([
        'notification-preferences',
      ]);
      if (previous) {
        const next: NotificationPreferences = { ...previous };
        for (const [k, v] of Object.entries(updates)) {
          if (k === 'all_enabled') {
            next.all_enabled = v as boolean;
          } else {
            const key = k as EmailToggleKey;
            next[key] = { ...next[key], ...(v as Partial<ChannelPrefs>) };
          }
        }
        queryClient.setQueryData(['notification-preferences'], next);
      }
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(['notification-preferences'], ctx.previous);
      }
      notify.error(err, 'Failed to update notification preferences');
    },
    onSettled: updated => {
      if (updated) {
        queryClient.setQueryData(['notification-preferences'], updated);
      }
    },
  });

  const toggleEmail = (key: EmailToggleKey) => {
    if (!prefs) return;
    updatePrefs({ [key]: { email: !prefs[key].email } });
  };

  // A failed load used to fall through to the skeleton below — `!prefs` is
  // true on error as well as while loading — so the panel pulsed forever with
  // no message and no way to retry. Same fix as the Account panel.
  if (isError && !prefs) {
    return (
      <ErrorState
        title="Couldn't load your notification preferences"
        description='Your preferences did not come back. Nothing has changed — try again.'
        primaryAction={{ label: 'Try again', onClick: () => void refetch() }}
      />
    );
  }

  if (isLoading || !prefs) {
    return (
      <div className='rounded-lg border border-border p-5'>
        <div className='mb-1 h-4 w-32 animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
        <div className='mb-3 h-3 w-56 animate-pulse rounded-sm bg-muted' />
        <div className='divide-y divide-border'>
          {[...Array(2)].map((_, i) => (
            <div key={i} className='flex items-center justify-between py-3'>
              <div className='space-y-1'>
                <div className='h-3.5 w-40 animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
                <div className='h-3 w-56 animate-pulse rounded-sm bg-muted' />
              </div>
              <div className='h-6 w-11 animate-pulse rounded-full bg-secondary dark:bg-white/10' />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className='rounded-lg border border-border p-5'>
      <h3 className='mb-1 text-sm font-semibold text-foreground'>
        Email Notifications
      </h3>
      <p className='mb-3 text-xs text-muted-foreground'>
        Choose which emails you want to receive from Xonera.
      </p>
      <div className='divide-y divide-border'>
        {EMAIL_TOGGLES.map(({ key, label, description }) => (
          <ToggleRow
            key={key}
            label={label}
            description={description}
            enabled={prefs[key].email}
            onToggle={() => toggleEmail(key)}
          />
        ))}
      </div>
    </div>
  );
}
